"use client";

import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import useSWR from "swr";
import { fetcher } from "@/lib/utils";
import { useSharedSubscription } from "@/app/hooks/useSharedNATS";
import { useDynamicConfig } from "@statsig/react-bindings";
import { TenantInfoKey } from "@/lib/enum/statsig/tenantInfoKey";
import { AuctioneerEvent, AuctioneerEventType, AuctionState } from "@prisma/client";

export enum LiveCloseState {
  ACTIVE = "ACTIVE",
  FAIR_WARNING = "FAIR_WARNING",
  CLOSED = "CLOSED",
  READY_TO_ADVANCE = "READY_TO_ADVANCE",
  FINISH_AUCTION = "FINISH_AUCTION",
}

type LiveCloseContextValue = {
  liveCloseState: LiveCloseState | null;
  setLiveCloseState: (state: LiveCloseState) => void;
  auctionState: AuctionState;
  isFinalLot: boolean;
};

const LiveCloseContext = createContext<LiveCloseContextValue | undefined>(undefined);

interface ProviderProps {
  auctionId: string;
  lotId: string;
  auctionState: AuctionState;
  finalLotId?: string;
  children: React.ReactNode;
}

const getLiveCloseState = (eventType: AuctioneerEventType | null, isFinalLot: boolean) => {
  console.log("eventType", eventType, "isFinalLot", isFinalLot);
  switch (eventType) {
    case AuctioneerEventType.FAIR_WARNING_STARTED:
      return LiveCloseState.FAIR_WARNING;
    case AuctioneerEventType.FAIR_WARNING_ENDED:
      return isFinalLot ? LiveCloseState.FINISH_AUCTION : LiveCloseState.CLOSED;
    case AuctioneerEventType.LOT_CLOSED:
      return LiveCloseState.READY_TO_ADVANCE;
    default:
      return LiveCloseState.ACTIVE;
  }
}

export function LiveCloseProvider({ auctionId, lotId, auctionState, finalLotId, children }: ProviderProps) {
  const config = useDynamicConfig("tenant_info");
  const [liveAuctionState, setLiveAuctionState] = useState(auctionState);

  const { data: initialState } = useSWR<{ state: LiveCloseState } | null>(
    auctionId && lotId ? `/api/auctions/${auctionId}/lots/${lotId}/auctioneer-events` : null,
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  const [liveCloseState, setLiveCloseState] = useState<LiveCloseState | null>(initialState?.state ?? null);

  // Keep local state in sync when SWR result updates (first load or revalidation)
  React.useEffect(() => {
    if (initialState) {
      setLiveCloseState(initialState?.state ?? LiveCloseState.ACTIVE);
    }
  }, [initialState]);

  const setStateStable = useCallback((state: LiveCloseState) => setLiveCloseState(state), []);

  const isFinalLot = useMemo(() => {
    if (!lotId || !finalLotId) return false;
    return Number(lotId) === Number(finalLotId);
  }, [lotId, finalLotId]);

  const value = useMemo<LiveCloseContextValue>(() => ({
    liveCloseState,
    setLiveCloseState: setStateStable,
    auctionState: liveAuctionState,
    isFinalLot,
  }), [liveCloseState, setStateStable, liveAuctionState, isFinalLot]);

  const onAuctioneerEvent = useCallback((message: AuctioneerEvent) => {
    // Ignore events for other lots in the same auction
    if (!message || Number(message.lotId) !== Number(lotId)) return;
    setLiveCloseState(getLiveCloseState(message?.eventType, isFinalLot));
  }, [lotId, isFinalLot]);

  const onAuctionChange = useCallback((payload: any) => {
    if (payload?.auctionState) {
      setLiveAuctionState(payload.auctionState);
    }
  }, [setLiveAuctionState]);

  useSharedSubscription({
    topic: `postgres.${config.get(TenantInfoKey.TenantId, "")}.Auction${auctionId}`,
    onMessage: onAuctionChange,
  });

  useSharedSubscription({
    topic: `postgres.${config.get(TenantInfoKey.TenantId, "")}.Auction${auctionId}.AuctioneerEvent`,
    onMessage: onAuctioneerEvent,
  })

  return <LiveCloseContext.Provider value={value}>{children}</LiveCloseContext.Provider>;
}

export function useLiveClose() {
  const ctx = useContext(LiveCloseContext);
  if (!ctx) throw new Error("useLiveClose must be used within a LiveCloseProvider");
  return ctx;
}


